import { BaseResponseCode } from 'shared/entities/network';
import { AccessRecoveryFastTrackServer } from 'shared/entities/accessRecovery';

import { ChannelKind } from '../channels';
import { TranslationString } from '../localization';
import {
  EmailDomainPolicyError,
  mapEmailDomainPolicyErrorToMessage
} from '../emailDomainPolicy';
import { ManagerSegmentVisibilityServer } from '../managerSegment';

export type UserServer = {
  name: string | null;
  email: string;
  confirmed: boolean;
  _id: string;
};

export type UserTokensServer = Partial<
  Record<ChannelKind.VK | ChannelKind.FB | ChannelKind.IG, boolean>
>;

export type ManagerExternalServer = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  link: string | null;
};

export type ManagerExternalsServer = Partial<
  Record<ChannelKind, ManagerExternalServer>
>;

export type UserExtraServer = {
  registered_via_elama?: boolean;
};

export type UserAuthServer<P = Record<string, any>> = {
  manager: UserServer & {
    extra: UserExtraServer;
    external_ids: ManagerExternalsServer;
  };
  tokens: UserTokensServer;
  manager_externals: ManagerExternalsServer;
} & P;

export type UserAuthInfoServer<P = Record<string, any>> = Omit<
  UserAuthServer<P>,
  'manager'
> & {
  manager: UserAuthServer<P>['manager'] & ManagerSegmentVisibilityServer;
};

export type UserLoginServer = Partial<UserAuthServer> &
  AccessRecoveryFastTrackServer;

export enum UserAcceptInvitationQueryServer {
  token = 'token',
  email = 'email'
}

export type UserAcceptInvitationServerType = {
  token: string;
  password?: string;
  name?: string;
  terms_accepted?: boolean;
  marketing_consent?: boolean;
};

export enum LoginErrors {
  CAPTCHA_ERROR = 'captcha_check_failed',
  INVALID_EMAIL_OR_PASSWORD = 'invalid_email_or_password'
}

export const mapLoginErrorToMessage =
  (code: LoginErrors): TranslationString =>
  (t) =>
    t(`user.loginErrors.${code}`, {
      ns: 'entities'
    });

export enum RegisterErrors {
  EMAIL_TAKEN = 'email_taken'
}

export type RegistrationErrors =
  | RegisterErrors
  | EmailDomainPolicyError.REGISTRATION_EMAIL_DOMAIN_NOT_ALLOWED;

export const mapRegistrationErrorToMessage = (
  code: RegistrationErrors
): TranslationString => {
  if (code === EmailDomainPolicyError.REGISTRATION_EMAIL_DOMAIN_NOT_ALLOWED) {
    return mapEmailDomainPolicyErrorToMessage(code);
  }

  return (t) =>
    t(`user.registrationErrors.${code}`, {
      ns: 'entities'
    });
};

export enum AuthErrors {
  NOT_AUTHORIZED = 'not_authorized'
}

export const mapAuthErrorToMessage =
  (code: AuthErrors): TranslationString =>
  (t) =>
    t(`user.authErrors.${code}`, {
      ns: 'entities'
    });

export enum BaseAuthLoginErrors {
  SHOULD_USE_OAUTH_VK = 'should_use_oauth_vk',
  SHOULD_USE_OAUTH_GOOGLE = 'should_use_oauth_google',
  SHOULD_USE_USUAL_LOGIN = 'should_use_usual_login',
  SHOULD_USE_OAUTH_TG = 'should_use_oauth_tg',
  SHOULD_USE_OAUTH_ELAMA = 'should_use_oauth_elama',
  GOOGLE_INVALID_DATA = 'google_invalid_data'
}

export const mapBaseLoginAuthErrors =
  (code: BaseAuthLoginErrors): TranslationString =>
  (t) =>
    t(`user.oauthErrors.${code}`, {
      ns: 'entities'
    });

export enum OAuthCommonError {
  NEED_EMAIL = 'need_email',
  NEED_NAME = 'need_name'
}

export enum OAuthRegistrationError {
  GOOGLE_REGISTRATION_FORBIDDEN = 'google_oauth_registration_forbidden',
  TG_REGISTRATION_FORBIDDEN = 'tg_oauth_registration_forbidden'
}

export const isOAuthRegistrationError = (
  code: unknown
): code is OAuthRegistrationError =>
  typeof code === 'string' &&
  Object.values(OAuthRegistrationError).includes(
    code as OAuthRegistrationError
  );

export const OAuthErrorCodes = [
  OAuthCommonError.NEED_EMAIL,
  OAuthCommonError.NEED_NAME,
  BaseResponseCode.conflict,
  EmailDomainPolicyError.REGISTRATION_EMAIL_DOMAIN_NOT_ALLOWED,
  OAuthRegistrationError.GOOGLE_REGISTRATION_FORBIDDEN,
  OAuthRegistrationError.TG_REGISTRATION_FORBIDDEN
];

export type OAuthError = (typeof OAuthErrorCodes)[number];

export const mapUpdatingOauthEmailToMessage = (
  code:
    | BaseResponseCode.conflict
    | EmailDomainPolicyError.REGISTRATION_EMAIL_DOMAIN_NOT_ALLOWED
): TranslationString => {
  if (code === EmailDomainPolicyError.REGISTRATION_EMAIL_DOMAIN_NOT_ALLOWED) {
    return mapEmailDomainPolicyErrorToMessage(code);
  }

  return (t) =>
    t(`user.updatingOauthManagerErrors.${code}`, {
      ns: 'entities'
    });
};

export enum AddUserErrorCode {
  notFound = 'not_found',
  noChatId = 'no_chat_id_specified_while_adding_users',
  incorrectChatId = 'incorrect_chat_id',
  chatIsNotGroup = 'chat_is_not_group',
  userIdIsNotValid = 'user_id_is_not_valid',
  chatMembersAccessDenied = 'chat_members_access_denied'
}

export const mapAddUserErrorCodeToMessage =
  (code: AddUserErrorCode): TranslationString =>
  (t) =>
    t(`user.addingUserErrors.${code}`, {
      ns: 'entities'
    });

export enum EmailUnsubscribeQueryParam {
  token = 'token',
  email = 'email'
}

export enum EmailUnsubscribeError {
  TOKEN_EXPIRES = 'expired_token',
  INVALID_TOKEN = 'invalid_token',
  EXTERNAL_SERVICE_ERROR = 'external_service_error'
}

export const mapEmailUnsubscribeErrorToMessage =
  (code: EmailUnsubscribeError): TranslationString =>
  (t) =>
    t(`user.emailUnsubscribeErrors.${code}`, {
      ns: 'entities'
    });

export type OauthInfoServer = {
  urls: {
    elama: string;
    google: string;
    vk: string;
  };
  access_recovery_token?: string | null;
};
